import axios from "@/plugins/axios";
import utils from "@/stores/utils";

/**
 * Messages of a campaign.
 *
 * A campaign can carry several messages, each targeting a slice of the
 * audience; order matters because the first matching message wins.
 */

const DEFAULT_FILTERS = { offset: 0, limit: 50, archived: false };

/** Absolute content URLs when the campaign store was never scoped (e.g. customer page). */
function contentsBaseUrl(state, params) {
    const campaignId = params?.campaign?.id ?? params?.campaign;
    if (campaignId) {
        return `/api/campaigns/${campaignId}/contents/`;
    }
    if (state.contents_url) {
        return state.contents_url;
    }
    return null;
}

function contentPreviewUrl(state, params) {
    const base = contentsBaseUrl(state, params);
    const contentId = params?.content?.id ?? params?.content;
    if (!base || contentId == null) {
        return null;
    }
    return `${base}${contentId}/preview/`;
}

const createState = (options = {}) => ({
    contents: [],
    contents_url: null,
    contents_count: 0,
    contents_filters: { ...DEFAULT_FILTERS },
    contents_loaded: false,
    contents_loading: false,
    contents_loading_errors: null,
    content_saving: false,
    content_saving_errors: {},
    preview: null,
    preview_loading: false,
    _storageOptions: options || {},
});

const mutations = {
    updateContents(state, contents) {
        state.contents = contents;
        state.contents_loaded = true;
        state.contents_loading_errors = null;
    },
    updateContentsCount(state, count) {
        state.contents_count = count;
    },
    updateContentsUrl(state, url) {
        state.contents_url = url;
    },
    updateContentsLoading(state, loading) {
        state.contents_loading = loading;
    },
    updateContentsLoadingErrors(state, errors) {
        state.contents_loading_errors = errors;
        state.contents_loading = false;
    },
    updateContentsFilters(state, filters) {
        state.contents_filters = filters;
    },
    updateContentSaving(state, saving) {
        state.content_saving = saving;
    },
    updateContentSavingErrors(state, errors) {
        state.content_saving_errors = errors || {};
    },
    updatePreview(state, preview) {
        state.preview = preview;
    },
    updatePreviewLoading(state, loading) {
        state.preview_loading = loading;
    },
};

const actions = {
    async fetchContents({ commit, state }) {
        if (!state.contents_url) {
            return;
        }
        commit("updateContentsLoading", true);
        try {
            const response = await axios.get(state.contents_url, {
                params: state.contents_filters,
            });
            commit("updateContents", response.data.results);
            commit("updateContentsCount", response.data.count);
            return response.data;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit("updateContentsLoadingErrors", error.details);
            throw error;
        } finally {
            commit("updateContentsLoading", false);
        }
    },

    async saveContent({ commit, dispatch, state }, params) {
        commit("updateContentSaving", true);
        commit("updateContentSavingErrors", {});
        try {
            const content = params.content;
            const response = content.id
                ? await axios.patch(`${state.contents_url}${content.id}/`, content)
                : await axios.post(state.contents_url, content);
            await dispatch("fetchContents");
            return response.data;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit("updateContentSavingErrors", error.details);
            throw error;
        } finally {
            commit("updateContentSaving", false);
        }
    },

    async deleteContent({ dispatch, state }, params) {
        try {
            await axios.delete(`${state.contents_url}${params.content.id}/`);
            await dispatch("fetchContents");
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async reorderContents({ dispatch, state }, params) {
        try {
            const response = await axios.post(`${state.contents_url}reorder/`, {
                contents: params.contents,
            });
            await dispatch("fetchContents");
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    /**
     * Render one message for display (card / modal) without touching the shared
     * composer preview state — several cards may load in parallel.
     */
    async fetchContentPreview({ state }, params) {
        try {
            const url = contentPreviewUrl(state, params);
            if (!url) {
                throw utils.handleError({
                    message: "Missing campaign for content preview",
                });
            }
            const customerId = params.customer?.id ?? params.customer ?? undefined;
            const contactId = params.contact?.id ?? params.contact ?? undefined;
            const response = await axios.get(url, {
                params: {
                    customer: customerId,
                    contact: contactId,
                },
            });
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    /** Recipients that would receive this message under current targeting order. */
    async fetchContentAudience({ state }, params) {
        try {
            const base = contentsBaseUrl(state, params);
            const contentId = params?.content?.id ?? params?.content;
            if (!base || contentId == null) {
                throw utils.handleError({
                    message: "Missing campaign for content audience",
                });
            }
            const response = await axios.get(`${base}${contentId}/audience/`);
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    /** Render the message exactly as it will be sent, for the composer preview. */
    async fetchPreview({ commit, state }, params) {
        commit("updatePreviewLoading", true);
        try {
            let response;
            const customerId = params.customer?.id ?? params.customer ?? undefined;
            const contactId = params.contact?.id ?? params.contact ?? undefined;
            const providerId = params.provider?.id ?? params.provider ?? undefined;
            const providerContactId =
                params.provider_contact?.id ?? params.provider_contact ?? undefined;
            if (params.draft) {
                const base = contentsBaseUrl(state, params);
                if (!base) {
                    throw utils.handleError({
                        message: "Missing campaign for draft preview",
                    });
                }
                const draft = params.draft;
                response = await axios.post(`${base}preview/`, {
                    id: draft.id || undefined,
                    name: draft.name,
                    subject: draft.subject,
                    preheader: draft.preheader,
                    body_markdown: draft.body_markdown,
                    template: draft.template,
                    accent_color: draft.accent_color,
                    targeting: draft.targeting || {},
                    customer: customerId,
                    contact: contactId,
                    provider: providerId,
                    provider_contact: providerContactId,
                });
            } else {
                const url = contentPreviewUrl(state, params);
                if (!url) {
                    throw utils.handleError({
                        message: "Missing campaign for content preview",
                    });
                }
                response = await axios.get(url, {
                    params: {
                        customer: customerId,
                        contact: contactId,
                        provider: providerId,
                        provider_contact: providerContactId,
                    },
                });
            }
            commit("updatePreview", response.data);
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updatePreviewLoading", false);
        }
    },

    async addAttachment({ dispatch, state }, params) {
        const data = new FormData();
        data.append("file", params.file);
        try {
            const response = await axios.post(
                `${state.contents_url}${params.content.id}/attachments/`,
                data,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
            await dispatch("fetchContents");
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async removeAttachment({ dispatch, state }, params) {
        try {
            await axios.delete(
                `${state.contents_url}${params.content.id}/attachments/${params.attachment.id}/`
            );
            await dispatch("fetchContents");
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async init({ commit }, params) {
        commit("updateContentsUrl", params?.url || null);
        commit("updateContentsFilters", { ...DEFAULT_FILTERS, ...(params?.filters || {}) });
        commit("updateContents", []);
        commit("updatePreview", null);
    },
};

export default (options = {}) => ({
    namespaced: true,
    state: createState(options),
    mutations,
    actions,
});
